import React, { useEffect, useRef, useState } from 'react';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link2,
  Image as ImageIcon,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Undo,
  Redo,
  Code,
} from 'lucide-react';

const RichTextEditor = ({ value, onChange, placeholder = 'Start writing...' }) => {
  const ref = useRef(null);
  const [empty, setEmpty] = useState(!value);

  // Set initial content only once
  useEffect(() => {
    if (ref.current && value !== undefined && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || '';
      setEmpty(!ref.current.textContent.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cmd = (command, arg = null) => {
    // eslint-disable-next-line no-restricted-syntax
    document.execCommand(command, false, arg);
    ref.current?.focus();
    triggerChange();
  };

  const triggerChange = () => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    setEmpty(!ref.current.textContent.trim());
    onChange?.(html);
  };

  const insertLink = () => {
    // eslint-disable-next-line no-alert
    const url = window.prompt('Enter URL');
    if (!url) return;
    cmd('createLink', url);
  };

  const insertImage = () => {
    // eslint-disable-next-line no-alert
    const url = window.prompt('Enter image URL');
    if (!url) return;
    cmd('insertImage', url);
  };

  const setBlock = (tag) => {
    cmd('formatBlock', tag);
  };

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-white focus-within:ring-2 focus-within:ring-[#1e73be]/20 focus-within:border-[#1e73be] transition">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        <ToolBtn onClick={() => setBlock('H1')} title="Heading 1"><Heading1 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => setBlock('H2')} title="Heading 2"><Heading2 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => setBlock('H3')} title="Heading 3"><Heading3 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => setBlock('P')} title="Paragraph">P</ToolBtn>
        <Divider />
        <ToolBtn onClick={() => cmd('bold')} title="Bold"><Bold className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd('italic')} title="Italic"><Italic className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd('underline')} title="Underline"><Underline className="w-4 h-4" /></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => cmd('insertUnorderedList')} title="Bulleted list"><List className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd('insertOrderedList')} title="Numbered list"><ListOrdered className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => setBlock('BLOCKQUOTE')} title="Quote"><Quote className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => setBlock('PRE')} title="Code block"><Code className="w-4 h-4" /></ToolBtn>
        <Divider />
        <ToolBtn onClick={insertLink} title="Insert link"><Link2 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={insertImage} title="Insert image"><ImageIcon className="w-4 h-4" /></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => cmd('undo')} title="Undo"><Undo className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd('redo')} title="Redo"><Redo className="w-4 h-4" /></ToolBtn>
      </div>
      <div className="relative">
        {empty && (
          <span className="pointer-events-none absolute top-3 left-4 text-slate-400">{placeholder}</span>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={triggerChange}
          onBlur={triggerChange}
          className="portal-content prose prose-slate max-w-none min-h-[240px] px-4 py-3 outline-none"
        />
      </div>
    </div>
  );
};

const ToolBtn = ({ onClick, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    className="w-8 h-8 grid place-items-center rounded text-slate-600 hover:bg-white hover:text-[#1e73be] hover:shadow-sm transition"
  >
    {children}
  </button>
);

const Divider = () => <div className="w-px h-5 mx-1 bg-slate-200" />;

export default RichTextEditor;
